import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import RuleFolderRoundedIcon from '@mui/icons-material/RuleFolderRounded';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useParams } from 'react-router-dom';

import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import Nav from '../nav';
import CoverPage from './coverPage';
import DisclaimerPage from './disclaimerPage';
import MethodologySection from './methodologyPage';
import {
  buildWebReconReportPdfData,
  METHOD_COLORS,
  scoreColor,
  alpha as pdfAlpha,
} from './webReconReportPdfHelpers';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const tocLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 0.85,
  color: '#10243E',
  textDecoration: 'none',
};

const markdownComponents = {
  p: (props) => <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7, mb: 1.15 }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 18, color: '#31445F' }}>
      <Typography component="span" variant="body1" sx={{ lineHeight: 1.7 }} {...props} />
    </li>
  ),
  strong: (props) => <Box component="strong" sx={{ color: '#10243E', fontWeight: 800 }} {...props} />,
  code: ({ inline, children, ...props }) => (
    inline ? (
      <code
        style={{
          background: '#f5f7fb',
          border: '1px solid #e3e8f2',
          borderRadius: 4,
          padding: '1px 4px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
        }}
        {...props}
      >
        {children}
      </code>
    ) : (
      <Box
        component="pre"
        sx={{
          bgcolor: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 2.5,
          p: 2,
          overflowX: 'auto',
          fontSize: '0.9rem',
          fontFamily: 'Consolas, Monaco, monospace',
          mb: 1.5,
        }}
        {...props}
      >
        <code>{children}</code>
      </Box>
    )
  ),
};

const MethodPill = ({ method }) => (
  <Chip
    size="small"
    label={method}
    sx={{
      bgcolor: METHOD_COLORS[method] || DEFAULT_PRIMARY,
      color: '#fff',
      fontWeight: 900,
      letterSpacing: 0.4,
      minWidth: 64,
    }}
  />
);

const ScorePill = ({ score, label }) => (
  <Chip
    size="small"
    label={`${label} • ${score}`}
    sx={{
      bgcolor: alpha(scoreColor(score), 0.12),
      color: scoreColor(score),
      border: '1px solid',
      borderColor: alpha(scoreColor(score), 0.24),
      fontWeight: 800,
    }}
  />
);

const MetricCard = ({ icon: Icon, label, value, caption, tone = DEFAULT_PRIMARY }) => (
  <Card
    sx={{
      p: 2.1,
      height: '100%',
      borderRadius: 3.5,
      boxShadow: 0,
      border: '1px solid',
      borderColor: alpha('#10243E', 0.08),
      bgcolor: '#fff',
    }}
  >
    <Stack direction="row" spacing={1.4} alignItems="flex-start">
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          bgcolor: alpha(tone, 0.1),
          color: tone,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.1, mt: 0.35 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, lineHeight: 1.5 }}>
          {caption}
        </Typography>
      </Box>
    </Stack>
  </Card>
);

const TableOfContents = ({ items }) => (
  <Box sx={{ p: '0.8in 0.9in' }} id="table-of-contents">
    <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', mb: 1 }}>
      Table of Contents
    </Typography>
    <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.7, mb: 2.4 }}>
      This recon report is organized to move from leadership context into methodology and then directly into the
      endpoint-by-endpoint threat model analysis.
    </Typography>

    {items.map((item) => (
      <Box key={item.id} component="a" href={`#${item.id}`} sx={tocLinkSx}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: item.level === 1 ? 700 : 500,
            pl: item.level > 1 ? 2 : 0,
          }}
        >
          {item.title}
        </Typography>
        <Box sx={{ flexGrow: 1, height: 1, bgcolor: '#dbe3ef' }} />
      </Box>
    ))}
  </Box>
);

const ExecutiveOverview = ({ report }) => {
  const cards = [
    {
      icon: RouteRoundedIcon,
      label: 'Endpoints Mapped',
      value: report.metrics.totalEndpoints,
      caption: `${report.metrics.activeMethodCount} observed HTTP methods across the application map.`,
      tone: report.brand.primary,
    },
    {
      icon: RadarRoundedIcon,
      label: 'Threat Hypotheses',
      value: report.metrics.totalHypotheses,
      caption: `${report.metrics.totalLikelyAttacks} reached the verification queue threshold.`,
      tone: '#C62828',
    },
    {
      icon: KeyRoundedIcon,
      label: 'Input Surfaces',
      value: report.metrics.endpointsWithParams,
      caption: `${report.metrics.stateChangingRoutes} state-changing route${report.metrics.stateChangingRoutes === 1 ? '' : 's'} identified.`,
      tone: '#1C7C3F',
    },
    {
      icon: RuleFolderRoundedIcon,
      label: 'Summary Coverage',
      value: `${report.metrics.endpointsWithSummaries}/${report.metrics.totalEndpoints}`,
      caption: 'Endpoints with usable functional summaries for business-context review.',
      tone: '#7A2CB5',
    },
  ];

  return (
    <Box id="executive-summary" sx={{ p: '0.72in 0.82in', bgcolor: '#fff' }}>
      <Card
        sx={{
          p: 2.6,
          mb: 2.4,
          borderRadius: 3.5,
          boxShadow: 0,
          border: '1px solid',
          borderColor: alpha('#10243E', 0.08),
          background: `linear-gradient(180deg, ${pdfAlpha(report.brand.primary, 0.06)} 0%, rgba(255,255,255,0.98) 58%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', mb: 0.5 }}>
              Executive Summary
            </Typography>
            <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
              {report.organizationName} • {report.testingType} • ID #{report.pentestId}
            </Typography>
            <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
              {report.reportDate}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              label={`GRADE: ${report.grade}`}
              sx={{
                bgcolor: report.gradeColor,
                color: '#fff',
                fontWeight: 900,
                borderRadius: '12px',
                mb: 0.8,
              }}
            />
            <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
              Risk Score: <strong>{report.riskScore ?? '-'}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
              Average top endpoint score: <strong>{report.metrics.averageTopScore}</strong>
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2.2 }}>
        {cards.map((card) => (
          <Grid key={card.label} item xs={12} sm={6}>
            <MetricCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mb: 2.1,
          px: 2,
          py: 1.6,
          borderRadius: 3,
          bgcolor: alpha(report.brand.primary, 0.045),
          border: '1px solid',
          borderColor: alpha(report.brand.primary, 0.14),
        }}
      >
        <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7 }}>
          This recon report is designed to stand on its own as a view into how the assessment team mapped the
          application, where user-controlled input appears, and which endpoint-specific attack paths look most
          plausible before or alongside full validation work.
        </Typography>
      </Box>

      <Stack spacing={1.4}>
        {report.executiveSummary.map((paragraph) => (
          <Typography key={paragraph} variant="body1" sx={{ color: '#31445F', lineHeight: 1.75 }}>
            {paragraph}
          </Typography>
        ))}
      </Stack>

      <Card
        sx={{
          mt: 2.5,
          p: 2.1,
          borderRadius: 3.5,
          boxShadow: 0,
          border: '1px solid',
          borderColor: alpha('#10243E', 0.08),
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
          Top At-Risk Endpoints
        </Typography>
        <Stack divider={<Divider flexItem />}>
          {report.topEndpoints.map((endpoint, index) => (
            <Stack key={endpoint.id} direction="row" spacing={1.4} alignItems="flex-start" sx={{ py: 1.05 }}>
              <Typography variant="body2" sx={{ color: '#51637D', fontWeight: 800, width: 20, pt: 0.2 }}>
                {index + 1}.
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: '#10243E', lineHeight: 1.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {endpoint.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
                  Top score {endpoint.score} • {endpoint.likelyAttackCount} verification-queue path{endpoint.likelyAttackCount === 1 ? '' : 's'} • {endpoint.totalAttackCount} total hypotheses
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Card>
    </Box>
  );
};

const EndpointSection = ({ endpoint, report }) => (
  <Box id={endpoint.id} sx={{ p: '0.72in 0.82in', bgcolor: '#fff' }}>
    <Stack spacing={0.8} sx={{ mb: 1.9 }}>
      <Typography variant="overline" sx={{ color: '#5C718E', letterSpacing: 1.4, fontWeight: 800 }}>
        Endpoint {endpoint.ordinal}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E' }}>
        Endpoint {endpoint.ordinal}
      </Typography>
      <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.6 }}>
        The section below captures the mapped route, the observed input surface, and the attack hypotheses
        associated to this endpoint.
      </Typography>
      <Box sx={{ width: 170, height: 4, borderRadius: '999px', bgcolor: report.brand.primary }} />
    </Stack>

    <Card
      sx={{
        p: 2.4,
        borderRadius: 3.5,
        boxShadow: 0,
        border: '1px solid',
        borderColor: alpha('#10243E', 0.08),
        bgcolor: alpha(report.brand.primary, 0.035),
        mb: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.2, mb: 0.6, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {endpoint.title}
          </Typography>
          <Typography variant="body1" sx={{ color: '#51637D', lineHeight: 1.65, overflowWrap: 'anywhere' }}>
            {endpoint.domain ? `${endpoint.domain} • ` : ''}
            {endpoint.path}
          </Typography>
        </Box>

        <MethodPill method={endpoint.method} />
      </Stack>

      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#10243E', mb: 0.75 }}>
        Description
      </Typography>
      <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7, mb: 1.5 }}>
        {endpoint.summaryText}
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 0.8 }}>
        {[
          {
            label: 'Likelihood',
            value: endpoint.topAttackScore,
            note: endpoint.totalAttackCount ? `${endpoint.totalAttackCount} total hypotheses modeled` : 'No hypotheses recorded',
          },
          {
            label: 'Input Surface',
            value: endpoint.paramTotal,
            note: `${endpoint.queryParams.length} query • ${endpoint.bodyParams.length} body • ${endpoint.pathParams.length} path`,
          },
          {
            label: 'Findings Present',
            value: endpoint.totalFindings,
            note: endpoint.stateChanging ? 'State-changing route' : 'Read-oriented route',
          },
        ].map((item) => (
          <Grid key={item.label} item xs={12} md={4}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: alpha('#10243E', 0.08),
                bgcolor: '#fff',
                height: '100%',
              }}
            >
              <Typography variant="caption" sx={{ color: '#5C718E', letterSpacing: 0.5 }}>
                {item.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mt: 0.3, mb: 0.45 }}>
                {item.value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.5 }}>
                {item.note}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {(endpoint.pathParams.length || endpoint.queryParams.length || endpoint.bodyParams.length) ? (
        <Box sx={{ mt: 0.6 }}>
          <Typography variant="caption" sx={{ color: '#5C718E', letterSpacing: 0.5 }}>
            Observed Parameters
          </Typography>
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {[
              ...endpoint.pathParams.map((value) => ({ label: `Path: ${value}` })),
              ...endpoint.queryParams.map((value) => ({ label: `Query: ${value}` })),
              ...endpoint.bodyParams.map((value) => ({ label: `Body: ${value}` })),
            ].map((item) => (
              <Chip
                key={`${endpoint.id}-${item.label}`}
                size="small"
                label={item.label}
                variant="outlined"
                sx={{
                  bgcolor: alpha(report.brand.primary, 0.045),
                  borderColor: alpha(report.brand.primary, 0.16),
                  '& .MuiChip-label': {
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Card>

    <Stack spacing={1.5}>
      {endpoint.attacks.length ? endpoint.attacks.map((attack) => (
        <Card
          key={attack.id}
          sx={{
            p: 2.1,
            borderRadius: 3.5,
            boxShadow: 0,
            border: '1px solid',
            borderColor: alpha('#10243E', 0.08),
            bgcolor: '#fff',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.4}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-start' }}
            sx={{ mb: 1.1 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.35, mb: 0.45 }}>
                {attack.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#51637D', lineHeight: 1.55 }}>
                {attack.verified ? 'Queued for verification threshold' : 'Below active verification threshold'} • {attack.label} confidence
              </Typography>
            </Box>

            <ScorePill score={attack.score} label={attack.label} />
          </Stack>

          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {attack.description.join('\n\n')}
          </ReactMarkdown>
        </Card>
      )) : (
        <Card
          sx={{
            p: 2.1,
            borderRadius: 3.5,
            boxShadow: 0,
            border: '1px solid',
            borderColor: alpha('#10243E', 0.08),
            bgcolor: alpha(report.brand.primary, 0.03),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mb: 0.75 }}>
            No attack hypotheses recorded
          </Typography>
          <Typography variant="body1" sx={{ color: '#31445F', lineHeight: 1.7 }}>
            No structured attack hypotheses were attached to this endpoint in the current threat-model dataset.
            That usually means the endpoint was inventoried successfully but did not yet receive deeper analyst
            attention or did not surface a strong attack narrative during the current pass.
          </Typography>
        </Card>
      )}
    </Stack>
  </Box>
);

const ReconReportWebPentest = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [threatModel, setThreatModel] = useState(null);
  const [exploits, setExploits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const fetchJson = (url) => fetch(url, { headers }).then((response) => response.json());

    Promise.all([
      fetchJson(`${BASEURL}/pentests/web/stats?pentest_id=${pentest_id}`),
      fetchJson(`${BASEURL}/pentests/web/endpoints?pentest_id=${pentest_id}`),
      fetchJson(`${BASEURL}/pentests/web/threat_model?pentest_id=${pentest_id}`),
      fetchJson(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`),
    ])
      .then(([statsRes, endpointsRes, threatRes, exploitsRes]) => {
        if ([statsRes, endpointsRes, threatRes, exploitsRes].some((result) => result?.message === 'Invalid token')) {
          navigate('/');
          return;
        }

        setStats(statsRes?.stats || statsRes || null);
        setEndpoints(Array.isArray(endpointsRes?.endpoints) ? endpointsRes.endpoints : []);
        setThreatModel(threatRes?.threat_model || threatRes || null);
        setExploits(Array.isArray(exploitsRes?.exploits) ? exploitsRes.exploits : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [navigate, pentest_id]);

  const report = useMemo(() => {
    if (!stats) return null;

    return buildWebReconReportPdfData({
      pentestId: pentest_id,
      primaryColor,
      companyName,
      clientName,
      stats,
      endpoints,
      threatModel,
      exploits,
    });
  }, [clientName, companyName, endpoints, exploits, pentest_id, primaryColor, stats, threatModel]);

  const handleDownloadPDF = async () => {
    if (!report) return;

    setDownloading(true);
    try {
      const [{ pdf }, { default: WebReconReportPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./webReconReportPdf'),
      ]);

      const blob = await pdf(<WebReconReportPdfDocument report={report} />).toBlob();
      downloadBlob(blob, `web_${pentest_id}_recon_report.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          className="report-config-card"
          sx={{
            width: '100%',
            maxWidth: '8.5in',
            mb: 2,
            boxShadow: 'none',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ p: 2, '& > [aria-label^="pdf-page-"]': { mb: 2.5 } }}>
        <Box className="pdf-page cover-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-0">
          <Card
            className="report-page-card report-page-card--cover"
            sx={{
              width: '100%',
              maxWidth: '8.5in',
              height: '10.8in',
              m: 0,
              p: 0,
              border: 0,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <CoverPage
              primary={primaryColor}
              company={companyName}
              clientName={report.clientName}
              reportDate={report.reportDate}
              eyebrow={report.cover.eyebrow}
              title={report.cover.title}
              subtitle={report.cover.subtitle}
              tags={report.cover.tags}
            />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-1">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <DisclaimerPage primary={primaryColor} company={companyName} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-2">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent sx={{ p: 0 }}>
              <TableOfContents items={report.tocItems} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-3">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <ExecutiveOverview report={report} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-4">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <MethodologySection
              primary={primaryColor}
              title="Methodology"
              intro="This section explains how the assessment team normally approaches a recon-focused web assessment from access and crawling through endpoint analysis and threat modeling."
              summary="We use a staged recon methodology so each phase builds on verified observations from the phase before it, allowing the assessment team to move from raw route discovery into structured endpoint analysis and defensible threat modeling."
              paragraphs={report.methodology}
            />
          </Card>
        </Box>

        {report.endpointRows.map((endpoint, index) => (
          <Box
            key={endpoint.id}
            className="pdf-page"
            sx={{ display: 'flex', justifyContent: 'center' }}
            aria-label={`pdf-page-${index + 5}`}
          >
            <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
              <EndpointSection endpoint={endpoint} report={report} />
            </Card>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default ReconReportWebPentest;
